import {
  Drawer,
  DrawerBody,
  DrawerHeader,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { useState } from 'react';
import { FeatureSwitches } from '../../../constants/feature-switches';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { UserFilterSettings } from '../../../models/order-filter';
import { IPHRequest } from '../../../models/purchase-history';
import { setPHFilterToSession } from '../../../utils/purchase-history/order-filter-utils';

import { OrderFilterGroup } from './OrderFilterGroup';
import { OrderFilterFooter } from './OrderFilterFooter';
import { OrderFilterUserSection } from './OrderFilterUserSection';
import { pushEventInfo } from '../../../utils/analytics';

interface IOrderFilterDrawer {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;
  dateFilterDot: {
    date: boolean;
    user: boolean;
  };
  setDateFilterDot: React.Dispatch<
    React.SetStateAction<{
      date: boolean;
      user: boolean;
    }>
  >;
  permissionRulesCheck: boolean;
}

export const OrderFilterDrawer = ({
  isDrawerOpen,
  setIsDrawerOpen,
  dateFilterDot,
  setDateFilterDot,
  permissionRulesCheck,
}: IOrderFilterDrawer) => {
  const { username, svocID } = useThdCustomer();

  const { pHRequest, setPHRequest, poJobFilters, paymentFilters } =
    usePurchaseHistoryContext();

  const poJobNameTileOptions = poJobFilters.map((filter) => ({
    label: filter,
    value: filter,
  }));

  const paymentTileOptions = paymentFilters.map((filter) => ({
    label: filter.last4,
    value: filter.last4,
  }));

  const [poJobNameFilter, setPOJobNameFilter] = useState<string[]>(
    pHRequest?.filters?.find((filter) => filter.name === 'poJobName')?.values ??
      [],
  );

  const [paymentFilter, setPaymentFilter] = useState<string[]>(
    pHRequest?.filters?.find((filter) => filter.name === 'payment')?.values ??
      [],
  );

  const isPOJobAndPaymentFilterEnabled =
    FeatureSwitches.enablePOJOBAndPaymentSearch === true;

  const [filterSettings, setFilterSettings] = useState<UserFilterSettings>({
    userView: pHRequest?.resultsFilter || 'allOrders',
  });

  const [isFilterChanged, setIsFilterChanged] = useState(false);

  const closeFilterDrawer = () => {
    setIsDrawerOpen(false);
  };

  const hasFilterChanges = (
    previousFilters: { name: string; values: string[] }[] = [],
    newFilters: { name: string; values: string[] }[] = [],
  ) => {
    if (previousFilters.length !== newFilters.length) {
      return true;
    }

    return previousFilters.some((filter) => {
      const newFilter = newFilters.find((fil) => fil.name === filter.name);
      if (!newFilter) return true;
      if (filter.values.length !== newFilter.values.length) return true;
      return filter.values.some((val) => !newFilter.values.includes(val));
    });
  };

  const getFilters = () => {
    const filters: { name: string; values: string[] }[] = [];
    if (poJobNameFilter.length > 0) {
      filters.push({ name: 'poJobName', values: poJobNameFilter });
    }
    if (paymentFilter.length > 0) {
      filters.push({ name: 'payment', values: paymentFilter });
    }
    return filters;
  };

  const handleUserViewChange = () => {
    setPHRequest((prevState: IPHRequest) => {
      const newFilters = isPOJobAndPaymentFilterEnabled
        ? getFilters()
        : prevState.filters;
      const hasChangedFilters =
        isPOJobAndPaymentFilterEnabled &&
        hasFilterChanges(prevState.filters ?? [], newFilters ?? []);
      const nextResultsFilter = filterSettings.userView ?? undefined;

      if (
        prevState.resultsFilter === nextResultsFilter &&
        prevState.pageNumber === 1 &&
        !hasChangedFilters
      ) {
        return prevState;
      }

      return {
        ...prevState,
        resultsFilter: nextResultsFilter,
        pageNumber: 1,
        filters: hasChangedFilters ? newFilters : prevState.filters,
      };
    });
    setPHFilterToSession(filterSettings);
    closeFilterDrawer();
    setIsFilterChanged(false);
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={closeFilterDrawer}
      style={{ zIndex: 20000000010 }}
    >
      <DrawerHeader title="Filter" onClose={closeFilterDrawer} />
      <DrawerBody>
        <>
          {permissionRulesCheck && (
            <OrderFilterUserSection
              showDot={dateFilterDot.user}
              userView={filterSettings.userView || 'allOrders'}
              username={username}
              setFilterSettings={setFilterSettings}
            />
          )}
          {isPOJobAndPaymentFilterEnabled && (
            <>
              <div className="sui-border-b-1 sui-border-solid sui-border-strong">
                <OrderFilterGroup
                  title="PO/Job Name"
                  tileOptions={poJobNameTileOptions}
                  selectedOptions={poJobNameFilter}
                  setSelectedOptions={setPOJobNameFilter}
                  setFilterChanged={setIsFilterChanged}
                />
              </div>
              <div className="sui-border-b-1 sui-border-solid sui-border-strong">
                <OrderFilterGroup
                  title="Payment"
                  tileOptions={paymentTileOptions}
                  selectedOptions={paymentFilter}
                  setSelectedOptions={setPaymentFilter}
                  setFilterChanged={setIsFilterChanged}
                />
              </div>
            </>
          )}
        </>
      </DrawerBody>
      <OrderFilterFooter
        filterSettings={filterSettings}
        pHRequestResultsFilter={pHRequest.resultsFilter}
        username={username}
        poJobNameFilter={poJobNameFilter}
        paymentFilter={paymentFilter}
        setFilterSettings={setFilterSettings}
        setPOJobNameFilter={setPOJobNameFilter}
        setPaymentFilter={setPaymentFilter}
        onViewResults={() => {
          if (filterSettings.userView !== pHRequest.resultsFilter) {
            setDateFilterDot((prevState) => ({ ...prevState, user: true }));
          }
          handleUserViewChange();

          const userChosenCount =
            filterSettings.userView !== 'allOrders' ? 1 : 0;
          pushEventInfo({
            category: { primaryCategory: 'button submission' },
            eventInfo: { eventName: 'my account settings' },
            myAccount: {
              settings: `purchase history: search filters: user ${userChosenCount}: pojobname ${poJobNameFilter.length}: payments ${paymentFilter.length}`,
            },
          });
        }}
        isFilterChanged={isFilterChanged}
        setFilterChanged={setIsFilterChanged}
      />
    </Drawer>
  );
};
